import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Briefcase,
  Check,
  X,
  EyeOff,
  AlertTriangle,
  User,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/job-titles")({
  component: JobTitlesPage,
});

interface JobTitle {
  id: string;
  name: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
}

interface TitleRequest {
  id: string;
  requestedName: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  createdAt: string;
}

function JobTitlesPage() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [reviewName, setReviewName] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "job-titles"],
    queryFn: () => apiGet<{ data: JobTitle[] }>("/api/admin/job-titles"),
  });

  const { data: requestsData, isLoading: requestsLoading } = useQuery({
    queryKey: ["admin", "job-title-requests"],
    queryFn: () => apiGet<{ data: TitleRequest[] }>("/api/admin/job-titles/requests"),
    refetchInterval: 60_000,
  });
  const pendingRequests = requestsData?.data ?? [];

  const reviewMutation = useMutation({
    mutationFn: ({ id, action, titleName }: { id: string; action: "approve" | "reject"; titleName?: string }) =>
      apiPatch(`/api/admin/job-titles/requests/${id}`, { action, titleName }),
    onSuccess: (_, vars) => {
      toast.success(vars.action === "approve" ? "Request approved — job title added" : "Request rejected");
      setReviewingId(null);
      setReviewName("");
      queryClient.invalidateQueries({ queryKey: ["admin", "job-title-requests"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "job-titles"] });
      queryClient.invalidateQueries({ queryKey: ["job-titles"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const titles = data?.data ?? [];

  const createMutation = useMutation({
    mutationFn: (name: string) => apiPost("/api/admin/job-titles", { name }),
    onSuccess: () => {
      toast.success("Job title created");
      setNewName("");
      setShowCreate(false);
      queryClient.invalidateQueries({ queryKey: ["admin", "job-titles"] });
      queryClient.invalidateQueries({ queryKey: ["job-titles"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiPatch(`/api/admin/job-titles/${id}`, { name }),
    onSuccess: () => {
      toast.success("Job title updated");
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey: ["admin", "job-titles"] });
      queryClient.invalidateQueries({ queryKey: ["job-titles"] });
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiPatch(`/api/admin/job-titles/${id}`, { isActive }),
    onSuccess: (_, vars) => {
      toast.success(vars.isActive ? "Job title activated" : "Job title deactivated");
      queryClient.invalidateQueries({ queryKey: ["admin", "job-titles"] });
      queryClient.invalidateQueries({ queryKey: ["job-titles"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/admin/job-titles/${id}`),
    onSuccess: () => {
      toast.success("Job title deactivated");
      queryClient.invalidateQueries({ queryKey: ["admin", "job-titles"] });
      queryClient.invalidateQueries({ queryKey: ["job-titles"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function startEdit(title: JobTitle) {
    setEditingId(title.id);
    setEditName(title.name);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName("");
  }

  function submitEdit(id: string) {
    const name = editName.trim();
    if (!name) return;
    updateMutation.mutate({ id, name });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const active = titles.filter((t) => t.isActive);
  const inactive = titles.filter((t) => !t.isActive);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Job Titles</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Define the job titles workers can select on their profile.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} disabled={showCreate}>
          <Plus className="h-4 w-4" />
          Add Job Title
        </Button>
      </div>

      {/* ── Pending custom title requests ─────────────────────────────── */}
      {!requestsLoading && pendingRequests.length > 0 && (
        <div className="rounded-xl border border-red-400/40 bg-red-500/10 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0" />
            <p className="text-sm font-semibold text-red-700 dark:text-red-400">
              {pendingRequests.length} pending job title request{pendingRequests.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="space-y-3">
            {pendingRequests.map((req) => (
              <div key={req.id} className="rounded-lg bg-surface-raised border border-border p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <User className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary">{req.userName ?? req.userEmail}</p>
                    <p className="text-xs text-muted-foreground">wants to add: <strong>"{req.requestedName}"</strong></p>
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {new Date(req.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {reviewingId === req.id ? (
                  <div className="space-y-2 pt-1">
                    <Input
                      value={reviewName}
                      onChange={e => setReviewName(e.target.value)}
                      placeholder="Title name (edit if needed)"
                      className="h-8 text-sm"
                      maxLength={100}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 h-8 text-xs"
                        disabled={!reviewName.trim() || reviewMutation.isPending}
                        onClick={() => reviewMutation.mutate({ id: req.id, action: "approve", titleName: reviewName.trim() })}
                      >
                        {reviewMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                        Approve &amp; Add
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        className="h-8 text-xs text-red-600 border-red-300 hover:bg-red-50"
                        disabled={reviewMutation.isPending}
                        onClick={() => reviewMutation.mutate({ id: req.id, action: "reject" })}
                      >
                        <X className="h-3 w-3" /> Reject
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setReviewingId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setReviewingId(req.id); setReviewName(req.requestedName); }}
                    className="text-xs text-paris-red-text hover:underline font-medium"
                  >
                    Review →
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showCreate && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex gap-2 items-center">
              <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
              <Input
                placeholder="e.g. Journeyman Pipefitter"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
                maxLength={100}
                onKeyDown={(e) => {
                  if (e.key === "Enter") createMutation.mutate(newName.trim());
                  if (e.key === "Escape") { setShowCreate(false); setNewName(""); }
                }}
                disabled={createMutation.isPending}
              />
              <button
                type="button"
                onClick={() => createMutation.mutate(newName.trim())}
                disabled={!newName.trim() || createMutation.isPending}
                className="p-1.5 rounded hover:bg-green-500/10 text-green-600 disabled:opacity-40"
              >
                {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={() => { setShowCreate(false); setNewName(""); }}
                className="p-1.5 rounded hover:bg-red-500/10 text-red-500"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {active.length === 0 && !showCreate ? (
        <Card>
          <CardContent className="py-12 flex flex-col items-center text-center gap-3">
            <Briefcase className="h-10 w-10 text-muted-foreground/40" />
            <p className="text-muted-foreground">No job titles yet.</p>
            <Button size="sm" variant="outline" onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4" /> Add your first job title
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {active.map((title) => (
                <li key={title.id} className="flex items-center gap-3 px-4 py-3">
                  <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                  {editingId === title.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        maxLength={100}
                        className="h-8 text-sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") submitEdit(title.id);
                          if (e.key === "Escape") cancelEdit();
                        }}
                        disabled={updateMutation.isPending}
                      />
                      <button
                        type="button"
                        onClick={() => submitEdit(title.id)}
                        disabled={!editName.trim() || updateMutation.isPending}
                        className="p-1.5 rounded hover:bg-green-500/10 text-green-600 disabled:opacity-40"
                      >
                        {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="p-1.5 rounded hover:bg-red-500/10 text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <span className="flex-1 text-sm text-text-primary">{title.name}</span>
                  )}
                  {editingId !== title.id && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => startEdit(title)}
                        className="p-1.5 rounded hover:bg-muted text-muted-foreground"
                        title="Rename"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteMutation.mutate(title.id)}
                        disabled={deleteMutation.isPending}
                        className="p-1.5 rounded hover:bg-red-500/10 text-red-500"
                        title="Deactivate"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {inactive.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
            <EyeOff className="h-3.5 w-3.5" /> Inactive
          </p>
          <Card>
            <CardContent className="p-0">
              <ul className="divide-y divide-border">
                {inactive.map((title) => (
                  <li key={title.id} className="flex items-center gap-3 px-4 py-3 opacity-60">
                    <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="flex-1 text-sm line-through text-muted-foreground">{title.name}</span>
                    <button
                      type="button"
                      onClick={() => toggleMutation.mutate({ id: title.id, isActive: true })}
                      disabled={toggleMutation.isPending}
                      className="text-xs text-paris-red-text hover:underline"
                    >
                      Restore
                    </button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
